import { Box, Typography, Grid } from "@mui/material";
import { useTranslation } from "react-i18next";
import meetimg from "../images/Fan Access/Meet-Greet-Ben-Barnes-Sunday-06-scaled.jpeg";
import privateimg from "../images/Fan Access/privateevent.jpeg";
import corporateimg from "../images/Fan Access/corporate.jpg";
import exclusiveimg from "../images/Fan Access/exclusive.jpg";
import digitalimg from "../images/Fan Access/digitalshoutout.avif";
import fansimg from "../images/Fan Access/fans.jpg";
import NavBar from "../NavBar/NavBar";
import { useEffect, useRef, useState } from "react";
import SEO from "../app/SEO";

const sectionImages = {
  meetGreets: meetimg,
  privateEvents: privateimg,
  corporate: corporateimg,
  exclusive: exclusiveimg,
  digital: digitalimg,
};

export default function FanAccessPage() {
  const { t, i18n } = useTranslation("fanaccess");
  const sections = t("sections", { returnObjects: true });
  const [lightNav, setLightNav] = useState(true);
  const heroRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => setLightNav(entry.isIntersecting),
      { threshold: 0.05 }
    );

    if (heroRef.current) observer.observe(heroRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <Box sx={{ backgroundColor: "#F6F5F3", minHeight: "100vh" }}>
      <SEO
        title="Fan Access Experiences | Helvieta Talent Agency"
        description="Helvieta curates exclusive fan access experiences connecting global celebrities and public personalities with their audiences through private events, meet and greets, digital shoutouts, and bespoke encounters."
        canonical={`https://helvieta.com/${i18n.language}/fan-access`}
        ogImage={fansimg}
      />

      <NavBar mode={lightNav ? "light" : "dark"} />

      {/* HERO */}
      <Box
        sx={{
          position: "relative",
          height: { xs: "50vh", md: "65vh" },
          backgroundColor: "#0B0B0B",
        }}
      >
        <Box
          ref={heroRef}
          sx={{
            position: "absolute",
            inset: 0,
            backgroundImage: `url(${fansimg})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            filter: "brightness(0.6)",
          }}
        />

        <Box
          sx={{
            position: "absolute",
            bottom: { xs: 40, md: 80 },
            left: { xs: 24, md: 80 },
          }}
        >
          <Typography
            component="h1" // ✅ SEO semantic
            sx={{
              fontFamily: "Playfair Display",
              fontSize: { xs: "2.5rem", md: "4.5rem" },
              color: "#FFFFFF",
              fontWeight: 700,
              letterSpacing: "0.04em",
              textTransform: "uppercase",
            }}
          >
            {t("title")}
          </Typography>
        </Box>
      </Box>

      {/* INTRO */}
      <Box sx={{ maxWidth: 900, px: { xs: 3, md: 8 }, py: { xs: 8, md: 10 } }}>
        <Typography
          component="h2" // ✅ SEO hierarchy
          sx={{
            fontFamily: "Playfair Display",
            fontSize: { xs: "1.6rem", md: "2.1rem" },
            fontWeight: 600,
            mb: 3,
          }}
        >
          {t("headline")}
        </Typography>

        <Typography
          sx={{
            fontFamily: "Inter",
            fontSize: { xs: "1rem", md: "1.15rem" },
            lineHeight: 1.9,
            color: "#444",
          }}
        >
          {t("intro")}
        </Typography>
      </Box>

      {/* SERVICES GRID */}
      <Box
        sx={{
          maxWidth: 1200,
          mx: "auto",
          px: { xs: 3, md: 8 },
          pb: { xs: 10, md: 14 },
        }}
      >
        <Grid container spacing={6}>
          {Object.keys(sections).map((key) => (
            <Grid item xs={12} md={6} key={key}>
              <Box
                sx={{
                  backgroundColor: "#FFFFFF",
                  boxShadow: "0 20px 40px rgba(0,0,0,0.06)",
                  height: "100%",
                }}
              >
                <Box
                  sx={{
                    width: "100%",
                    aspectRatio: "16 / 10",
                    overflow: "hidden",
                  }}
                >
                  <Box
                    component="img"
                    src={sectionImages[key]}
                    alt={`Helvieta ${t(`sections.${key}.title`)}`} // ✅ keyword-rich alt
                    loading="lazy"
                    sx={{ width: "100%", height: "100%", objectFit: "cover" }}
                  />
                </Box>

                <Box sx={{ p: { xs: 4, md: 5 } }}>
                  <Typography
                    component="h3"
                    sx={{
                      fontFamily: "Playfair Display",
                      fontSize: "1.4rem",
                      fontWeight: 600,
                      mb: 2,
                    }}
                  >
                    {t(`sections.${key}.title`)}
                  </Typography>

                  <Typography
                    sx={{
                      fontFamily: "Inter",
                      fontSize: "0.95rem",
                      lineHeight: 1.8,
                      color: "#555",
                    }}
                  >
                    {t(`sections.${key}.description`)}
                  </Typography>
                </Box>
              </Box>
            </Grid>
          ))}
        </Grid>
      </Box>
    </Box>
  );
}
